import React, { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";



// ── Status Badge ──
const StatusBadge = ({ active }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${
      active
        ? "bg-[#F0FEF4] text-[#008236] border-[#0a7822a6] border"
        : "bg-gray-100 text-gray-500 border border-gray-200"
    }`}
  >
    <span
      className={`size-1.5 rounded-full ${active ? "bg-[#0a7822a6]" : "bg-gray-400"}`}
    />
    {active ? "Active" : "Inactive"}
  </span>
);

const STATUS_TABS = [
  { label: "All", value: "" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const formatDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const formatCurrency = (val, code = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: code }).format(val ?? 0);

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const parseNumber = (value) => {
  if (value == null || value === "") return 0;
  const num = typeof value === "string" ? parseFloat(value.replace(/,/g, "")) : Number(value);
  return Number.isFinite(num) ? num : 0;
};

const toBoolean = (value, fallback = true) => {
  if (value == null) return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.toLowerCase().trim();
    if (["true", "yes", "1", "active"].includes(normalized)) return true;
    if (["false", "no", "0", "inactive"].includes(normalized)) return false;
  }
  return Boolean(value);
};

const formatAddress = (addr) => {
  if (!addr) return "—";
  const parts = [
    addr.Line1 || addr.line1 || addr.Addr1 || addr.addr1,
    addr.Line2 || addr.line2 || addr.Addr2 || addr.addr2,
    addr.City || addr.city,
    addr.CountrySubDivisionCode || addr.State || addr.state,
    addr.PostalCode || addr.postalCode,
    addr.Country || addr.country,
  ].filter(Boolean);
  return parts.join(", ") || "—";
};

const getRaw = (customer) => customer?.raw || {};

const getCustomerName = (customer) => {
  const item = customer || {};
  const raw = getRaw(item);
  const fullName = [item.firstName, item.lastName].filter(Boolean).join(" ");

  return (
    item.fullyQualifiedName ||
    item.FullName ||
    raw.FullName ||
    item.name ||
    item.Name ||
    raw.Name ||
    fullName ||
    item.company ||
    raw.CompanyName ||
    "—"
  );
};

const getCustomerCompany = (customer) => {
  const item = customer || {};
  const raw = getRaw(item);
  return item.company || item.CompanyName || raw.CompanyName || "";
};

const getCustomerEmail = (customer) => {
  const item = customer || {};
  const raw = getRaw(item);
  return item.email || item.Email || raw.Email || "";
};

const getCustomerPhone = (customer) => {
  const item = customer || {};
  const raw = getRaw(item);
  return item.phone || item.mobile || item.Phone || raw.Phone || "";
};

const getCustomerActive = (customer) => {
  const item = customer || {};
  const raw = getRaw(item);
  return toBoolean(item.active ?? item.IsActive ?? raw.IsActive, true);
};

const getCustomerBillAddress = (customer) => {
  const item = customer || {};
  const raw = getRaw(item);
  return item.billAddress || item.BillAddress || item.BillAddr || raw.BillAddress || raw.BillAddr;
};

const getCustomerShipAddress = (customer) => {
  const item = customer || {};
  const raw = getRaw(item);
  return item.shipAddress || item.ShipAddress || item.ShipAddr || raw.ShipAddress || raw.ShipAddr;
};

const getCustomerUpdatedAt = (customer) => {
  const item = customer || {};
  const raw = getRaw(item);
  return (
    item.lastUpdatedTime ||
    item.updatedAt ||
    item.TimeModified ||
    raw.TimeModified ||
    item.createTime ||
    item.createdAt ||
    item.TimeCreated ||
    raw.TimeCreated
  );
};

const getCustomerCreatedAt = (customer) => {
  const item = customer || {};
  const raw = getRaw(item);
  return item.createTime || item.createdAt || item.TimeCreated || raw.TimeCreated;
};

const getCustomerBalance = (customer) => {
  const item = customer || {};
  const raw = getRaw(item);
  return parseNumber(item.balance ?? item.Balance ?? raw.Balance ?? item.totalBalance ?? raw.TotalBalance);
};

const getCustomerKey = (customer, index) => {
  const raw = getRaw(customer);
  return customer?.qbId || customer?.ListID || raw.ListID || customer?._id || index;
};

export default function QBDCustomerList() {
  const history = useNavigate();
  const user = useSelector((state) => state.user);
  const companyId = user?.id || user?._id;

  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const fetchCustomers = async () => {
    loader(true);
    await ApiClient.get(`qb-desktop/list/customers?companyId=${companyId}`)
      .then((res) => {
        if (res?.success) setCustomers(res?.data || []);
      })
      .catch(console.error);
    loader(false);
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const filteredCustomers = customers.filter((c) => {
    const q = searchQuery.toLowerCase();
    const searchableText = [
      getCustomerName(c),
      getCustomerCompany(c),
      getCustomerEmail(c),
      getCustomerPhone(c),
      c?.qbId,
      getRaw(c).ListID,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const matchesSearch =
      !q ||
      searchableText.includes(q);
    const matchesStatus =
      statusFilter === ""
        ? true
        : statusFilter === "active"
          ? getCustomerActive(c)
          : !getCustomerActive(c);
    return matchesSearch && matchesStatus;
  });

  const activeCount = customers.filter(getCustomerActive).length;

  const closeModal = () => {
    setIsViewModalOpen(false);
    setSelectedCustomer(null);
  };

  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px] mx-auto">
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
          <div className="flex flex-col gap-1">
            <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold">
              QB Desktop – Customers
            </h2>
            <p className="text-sm text-gray-600">
              {customers.length} customer{customers.length !== 1 ? "s" : ""} synced
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => history("/qb-desktop/customers/add")}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs sm:text-sm font-semibold rounded-xl shadow-sm transition-all active:scale-95"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Customer
            </button>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F0FEF4] border border-[#008236]/20 rounded-xl">
              <span className="size-2 rounded-full bg-[#008236] animate-pulse" />
              <span className="text-xs font-medium text-[#008236]">QB Desktop Connected</span>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {[
            {
              label: "Total Customers",
              value: customers.length,
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-[#115B9D]">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              ),
              bg: "bg-[#115B9D17]",
            },
            {
              label: "Active Customers",
              value: activeCount,
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-[#008236]">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              ),
              bg: "bg-[#f0fdf4]",
            },
            {
              label: "Inactive Customers",
              value: customers.length - activeCount,
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
              ),
              bg: "bg-amber-50",
            },
          ].map((card) => (
            <div key={card.label} className="border border-gray-200 rounded-2xl bg-white px-5 py-4 flex items-center gap-4">
              <div className={`size-10 rounded-xl ${card.bg} flex items-center justify-center shrink-0`}>
                {card.icon}
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">{card.label}</p>
                <p className="text-xl font-semibold text-gray-900 mt-0.5">{card.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
          {/* Filters */}
          <div className="flex justify-between flex-wrap gap-3 mb-4">
            <div className="max-sm:w-full w-full sm:w-fit overflow-auto flex items-center gap-1 bg-gray-100 rounded-xl p-1.5">
              {STATUS_TABS.map((tab) => {
                const count =
                  tab.value === ""
                    ? customers.length
                    : tab.value === "active"
                    ? activeCount
                    : customers.length - activeCount;
                const isActive = statusFilter === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => setStatusFilter(tab.value)}
                    className={`whitespace-nowrap inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      isActive
                        ? "bg-white text-gray-900 shadow-sm font-semibold"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tab.label}
                    <span
                      className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                        isActive
                          ? "bg-[#115B9D]/10 text-[#115B9D] font-bold"
                          : "bg-gray-200/70 text-gray-600"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="relative w-full sm:w-[350px]">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="text"
                placeholder="Search by name, email, phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
              />
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-hidden border border-gray-200 rounded-xl">
            <table className="w-full table-fixed">
              <colgroup>
                <col style={{ width: "6%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "16%" }} />
              </colgroup>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["Actions", "Customer", "Contact", "Address", "Status", "Last Updated"].map((h) => (
                    <th key={h} className="text-left py-3 px-3">
                      <span className="text-xs text-gray-700 font-medium block">{h}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-500">No customers found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((item, index) => {
                    const name = getCustomerName(item);
                    const company = getCustomerCompany(item);
                    const email = getCustomerEmail(item);
                    const phone = getCustomerPhone(item);
                    const active = getCustomerActive(item);
                    const address = formatAddress(getCustomerBillAddress(item) || getCustomerShipAddress(item));

                    return (
                      <tr key={getCustomerKey(item, index)} className="hover:bg-gray-50/60 transition-colors align-middle">
                        <td className="py-2.5 px-3 align-middle">
                          <button
                            type="button"
                            onClick={() => { setSelectedCustomer(item); setIsViewModalOpen(true); }}
                            title="View Details"
                            className="size-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>
                        </td>
                        <td className="py-2.5 px-3 align-middle overflow-hidden">
                          <div className="flex items-start gap-2 min-w-0">
                            <div className="size-8 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                              <span className="text-[10px] font-semibold text-[#115B9D]">{getInitials(name)}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-gray-800 break-words leading-tight">{name}</p>
                              {company && <p className="text-[11px] text-gray-600 break-words leading-tight">{company}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="py-2.5 px-3 align-middle overflow-hidden">
                          <p className="text-xs text-gray-700 break-all leading-tight">{email || "—"}</p>
                          {phone && <p className="text-[11px] text-gray-600 mt-0.5 whitespace-nowrap">{phone}</p>}
                        </td>
                        <td className="py-2.5 px-3 align-middle overflow-hidden">
                          <p className="text-xs text-gray-500 break-words leading-snug">{address}</p>
                        </td>
                        <td className="py-2.5 px-3 align-middle">
                          <StatusBadge active={active} />
                        </td>
                        <td className="py-2.5 px-3 align-middle">
                          <p className="text-xs text-gray-700 break-words leading-tight">{formatDate(getCustomerUpdatedAt(item))}</p>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3 mt-3">
            {filteredCustomers.length === 0 ? (
              <div className="border border-gray-200 rounded-xl py-16 flex flex-col items-center gap-3">
                <p className="text-sm text-gray-500">No customers found</p>
              </div>
            ) : (
              filteredCustomers.map((item, index) => {
                const name = getCustomerName(item);
                const company = getCustomerCompany(item);
                const email = getCustomerEmail(item);
                const active = getCustomerActive(item);

                return (
                  <div key={getCustomerKey(item, index)} className="border border-gray-200 rounded-xl p-4 bg-white">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-start gap-2.5 min-w-0">
                        <div className="size-9 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                          <span className="text-xs font-semibold text-[#115B9D]">{getInitials(name)}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 break-words">{name}</p>
                          {company && <p className="text-xs text-gray-600">{company}</p>}
                        </div>
                      </div>
                      <button type="button" onClick={() => { setSelectedCustomer(item); setIsViewModalOpen(true); }} className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Email</span>
                      <p className="text-xs text-gray-700 break-all max-w-[60%] text-right">{email || "—"}</p>
                    </div>
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Status</span>
                      <StatusBadge active={active} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* View Modal */}
      <Transition appear show={isViewModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6">
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <div className="size-12 rounded-2xl bg-[#115B9D17] flex items-center justify-center">
                        <span className="text-sm font-bold text-[#115B9D]">{getInitials(getCustomerName(selectedCustomer))}</span>
                      </div>
                      <div>
                        <Dialog.Title className="text-lg font-semibold text-gray-900">{getCustomerName(selectedCustomer)}</Dialog.Title>
                        {getCustomerCompany(selectedCustomer) && <p className="text-sm text-gray-500">{getCustomerCompany(selectedCustomer)}</p>}
                      </div>
                    </div>
                    <button type="button" onClick={closeModal} className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                    </button>
                  </div>
                  <div className="space-y-2.5">
                    {[
                      { label: "QB ID", value: selectedCustomer?.qbId || getRaw(selectedCustomer).ListID },
                      { label: "Email", value: getCustomerEmail(selectedCustomer) },
                      { label: "Phone", value: getCustomerPhone(selectedCustomer) },
                      { label: "Company", value: getCustomerCompany(selectedCustomer) },
                      { label: "Balance", value: formatCurrency(getCustomerBalance(selectedCustomer)) },
                      { label: "Bill Address", value: formatAddress(getCustomerBillAddress(selectedCustomer)) },
                      { label: "Ship Address", value: formatAddress(getCustomerShipAddress(selectedCustomer)) },
                      { label: "Created", value: formatDate(getCustomerCreatedAt(selectedCustomer)) },
                      { label: "Last Updated", value: formatDate(getCustomerUpdatedAt(selectedCustomer)) },
                      { label: "Status", value: getCustomerActive(selectedCustomer) ? "Active" : "Inactive" },
                    ].map(({ label, value }) =>
                      value ? (
                        <div key={label} className="flex justify-between items-start py-2 border-b border-gray-50 last:border-0">
                          <p className="text-xs text-gray-500 font-medium">{label}</p>
                          <p className="text-sm text-gray-800 font-medium text-right max-w-[60%] break-words">{value}</p>
                        </div>
                      ) : null
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
}
